import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    Music, 
    Play, 
    Users, 
    Shield, 
    Zap, 
    CheckCircle2,
    ArrowRight,
    Headphones,
    Film,
    Tv,
    Radio,
    Star,
    Lock,
    TrendingUp,
    Globe,
    Clock,
    FileAudio,
    UserCheck,
    Building2,
    Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function LandingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);

    // Auto-rotate testimonials
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const stats = [
        { value: '500+', label: 'Sync Placements', icon: Film },
        { value: '$2M+', label: 'Artist Earnings', icon: TrendingUp },
        { value: '150+', label: 'Active Projects', icon: FileAudio },
        { value: '48hr', label: 'Avg Response Time', icon: Clock }
    ];

    const testimonials = [
        {
            quote: "Pitch Room connected us with incredible indie talent we never would have found otherwise. The quality of submissions has been exceptional.",
            author: "Sarah M.",
            role: "Music Supervisor, Netflix",
            avatar: "SM"
        },
        {
            quote: "Got my first sync placement within 3 weeks of joining. The invite-only model means less competition and more real opportunities.",
            author: "Marcus J.",
            role: "Producer & Composer",
            avatar: "MJ"
        },
        {
            quote: "Finally, a platform that respects both sides. Clean interface, quality submissions, and artists who understand sync.",
            author: "David L.",
            role: "Creative Director, Sony Music",
            avatar: "DL"
        }
    ];

    const posterFeatures = [
        { icon: UserCheck, title: 'Curated Talent Pool', desc: 'Access vetted artists who understand sync licensing' },
        { icon: Zap, title: 'Streamlined Workflow', desc: 'Kanban boards, bulk actions, and team collaboration' },
        { icon: Shield, title: 'Quality Control', desc: 'Invite-only ensures professional submissions' },
        { icon: Clock, title: 'Fast Turnaround', desc: 'Get submissions within hours, not weeks' }
    ];

    const artistFeatures = [
        { icon: Lock, title: 'Exclusive Access', desc: 'Invitation-only opportunities from real projects' },
        { icon: Film, title: 'Direct Placements', desc: 'Skip the gatekeepers, pitch directly to supervisors' },
        { icon: TrendingUp, title: 'Career Growth', desc: 'Build relationships with top music buyers' },
        { icon: Globe, title: 'Global Reach', desc: 'Film, TV, ads, and gaming opportunities worldwide' }
    ];

    const placements = [
        { type: 'Film', icon: Film, examples: ['Feature Films', 'Documentaries', 'Trailers'] },
        { type: 'Television', icon: Tv, examples: ['Series', 'Reality TV', 'Promos'] },
        { type: 'Advertising', icon: Radio, examples: ['Commercials', 'Brand Campaigns', 'Social'] },
        { type: 'Gaming', icon: Headphones, examples: ['Video Games', 'Trailers', 'In-Game'] }
    ];

    return (
        <div className="min-h-screen bg-[#0A0A0A]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-[#222]">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F51E1E] to-[#FF6B6B] flex items-center justify-center">
                                <Music className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-white">Pitch Room</span>
                        </Link>

                        <div className="flex items-center gap-6">
                            <Link to="/pricing" className="text-[#A3A3A3] hover:text-white transition-colors">
                                Pricing
                            </Link>
                            <Link to="/poster-signup" className="text-[#A3A3A3] hover:text-white transition-colors">
                                For Music Buyers
                            </Link>
                            <Link to="/login">
                                <Button variant="outline" className="border-[#333] text-white hover:bg-[#1A1A1A]">
                                    Login
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#F51E1E]/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-7xl mx-auto px-6">
                    <div className="text-center max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-6"
                        >
                            <Badge className="bg-[#F51E1E]/10 text-[#F51E1E] border-[#F51E1E]/20 px-4 py-2">
                                <Sparkles className="w-4 h-4 mr-2" />
                                The Invite-Only Sync Licensing Platform
                            </Badge>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
                        >
                            Where Music Meets
                            <span className="block bg-gradient-to-r from-[#F51E1E] to-[#FF6B6B] bg-clip-text text-transparent">
                                Picture
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-xl text-[#A3A3A3] mb-8 max-w-2xl mx-auto"
                        >
                            The exclusive platform connecting music supervisors with exceptional artists. 
                            Curated talent. Real opportunities. No noise.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="flex flex-col sm:flex-row items-center justify-center gap-4"
                        >
                            <Link to="/poster-signup">
                                <Button size="lg" className="bg-[#F51E1E] hover:bg-[#D41212] text-white px-8 py-6 text-lg">
                                    <Building2 className="w-5 h-5 mr-2" />
                                    I'm a Music Buyer
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <div className="flex items-center gap-2 text-[#666]">
                                <Lock className="w-4 h-4" />
                                <span className="text-sm">Artists & Producers are invite-only</span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Stats */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
                    >
                        {stats.map((stat, index) => (
                            <div 
                                key={index}
                                className="bg-[#111]/50 border border-[#222] rounded-2xl p-6 text-center backdrop-blur-sm"
                            >
                                <stat.icon className="w-8 h-8 text-[#F51E1E] mx-auto mb-3" />
                                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                                <div className="text-sm text-[#666]">{stat.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Placement Types */}
            <section className="py-20 border-t border-[#1A1A1A]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-white mb-4">Sync Opportunities Across All Media</h2>
                        <p className="text-[#888] max-w-2xl mx-auto">
                            From blockbuster films to viral campaigns, our platform covers every corner of the sync world.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {placements.map((placement, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-gradient-to-b from-[#1A1A1A] to-[#111] border border-[#222] rounded-2xl p-6 group hover:border-[#F51E1E]/30 transition-all"
                            >
                                <placement.icon className="w-10 h-10 text-[#F51E1E] mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-semibold text-white mb-3">{placement.type}</h3>
                                <ul className="space-y-1">
                                    {placement.examples.map((ex, i) => (
                                        <li key={i} className="text-sm text-[#666] flex items-center gap-2">
                                            <div className="w-1 h-1 rounded-full bg-[#F51E1E]" />
                                            {ex}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* For Music Buyers Section */}
            <section className="py-20 bg-gradient-to-b from-[#0A0A0A] to-[#111]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 mb-6">
                                For Music Supervisors & Agencies
                            </Badge>
                            <h2 className="text-4xl font-bold text-white mb-6">
                                Find the Perfect Track,
                                <span className="text-purple-400"> Fast</span>
                            </h2>
                            <p className="text-[#A3A3A3] mb-8 text-lg">
                                Stop drowning in unsolicited demos. Pitch Room gives you access to a curated 
                                pool of sync-ready artists who understand the business and deliver on brief.
                            </p>

                            <div className="space-y-4 mb-8">
                                {posterFeatures.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-start gap-4"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                                            <feature.icon className="w-5 h-5 text-purple-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-white">{feature.title}</h4>
                                            <p className="text-sm text-[#888]">{feature.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <Link to="/poster-signup">
                                <Button className="bg-purple-500 hover:bg-purple-600 text-white">
                                    Start Posting Projects
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>

                        <div className="relative">
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-6 shadow-2xl">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="font-semibold text-white">Active Projects</h3>
                                    <Badge className="bg-green-500/20 text-green-400">Live</Badge>
                                </div>
                                <div className="space-y-3">
                                    {['Upbeat Pop for Netflix Series', 'Emotional Piano for Documentary', 'Indie Rock for Car Commercial'].map((project, i) => (
                                        <div key={i} className="bg-[#262626] rounded-lg p-4 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded bg-[#F51E1E]/20 flex items-center justify-center">
                                                    <Music className="w-4 h-4 text-[#F51E1E]" />
                                                </div>
                                                <span className="text-sm text-white">{project}</span>
                                            </div>
                                            <span className="text-xs text-[#666]">{12 + i * 5} submissions</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl" />
                        </div>
                    </div>
                </div>
            </section>

            {/* For Artists Section */}
            <section className="py-20 border-t border-[#1A1A1A]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div className="order-2 md:order-1 relative">
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-6 shadow-2xl">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F51E1E] to-[#FF6B6B] flex items-center justify-center">
                                        <Lock className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white">Exclusive Invitation</h3>
                                        <p className="text-sm text-[#888]">You've been invited to Pitch Room</p>
                                    </div>
                                </div>
                                <div className="bg-[#262626] rounded-lg p-4 mb-4">
                                    <p className="text-sm text-[#A3A3A3] mb-2">Project Brief:</p>
                                    <p className="text-white font-medium">"Anthemic indie rock for major car brand campaign"</p>
                                    <div className="flex items-center gap-4 mt-3 text-xs text-[#666]">
                                        <span>Budget: $15,000+</span>
                                        <span>Deadline: 2 weeks</span>
                                    </div>
                                </div>
                                <Button className="w-full bg-[#F51E1E] hover:bg-[#D41212] text-white">
                                    Submit Your Track
                                </Button>
                            </div>
                            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#F51E1E]/20 rounded-full blur-2xl" />
                        </div>

                        <div className="order-1 md:order-2">
                            <Badge className="bg-[#F51E1E]/10 text-[#F51E1E] border-[#F51E1E]/20 mb-6">
                                For Artists & Producers
                            </Badge>
                            <h2 className="text-4xl font-bold text-white mb-6">
                                Real Opportunities,
                                <span className="text-[#F51E1E]"> No Gatekeepers</span>
                            </h2>
                            <p className="text-[#A3A3A3] mb-8 text-lg">
                                Pitch Room is invite-only for artists because we believe in quality over quantity. 
                                When you're in, you get direct access to sync opportunities that actually pay.
                            </p>

                            <div className="space-y-4 mb-8">
                                {artistFeatures.map((feature, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-start gap-4"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-[#F51E1E]/10 flex items-center justify-center flex-shrink-0">
                                            <feature.icon className="w-5 h-5 text-[#F51E1E]" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-white">{feature.title}</h4>
                                            <p className="text-sm text-[#888]">{feature.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl p-4 flex items-center gap-4">
                                <Lock className="w-6 h-6 text-[#666]" />
                                <div>
                                    <p className="text-white font-medium">Artist Access is Invite-Only</p>
                                    <p className="text-sm text-[#888]">Get invited by a music supervisor or existing member</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-20 bg-[#111]">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-white mb-4">Trusted by Industry Leaders</h2>
                    </div>

                    <div className="relative">
                        <motion.div
                            key={activeTestimonial}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-[#1A1A1A] border border-[#333] rounded-2xl p-8 text-center"
                        >
                            <div className="flex justify-center mb-4">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                                ))}
                            </div>
                            <p className="text-xl text-white mb-6 italic">
                                "{testimonials[activeTestimonial].quote}"
                            </p>
                            <div className="flex items-center justify-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F51E1E] to-[#FF6B6B] flex items-center justify-center text-white font-bold">
                                    {testimonials[activeTestimonial].avatar}
                                </div>
                                <div className="text-left">
                                    <p className="font-medium text-white">{testimonials[activeTestimonial].author}</p>
                                    <p className="text-sm text-[#888]">{testimonials[activeTestimonial].role}</p>
                                </div>
                            </div>
                        </motion.div>

                        <div className="flex justify-center gap-2 mt-6">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className={`w-2 h-2 rounded-full transition-all ${
                                        index === activeTestimonial ? 'bg-[#F51E1E] w-6' : 'bg-[#444]'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F51E1E]/10 rounded-full blur-3xl" />
                </div>

                <div className="relative max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Elevate Your Sync Game?
                    </h2>
                    <p className="text-xl text-[#A3A3A3] mb-8 max-w-2xl mx-auto">
                        Join the platform that's changing how music meets media.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/poster-signup">
                            <Button size="lg" className="bg-[#F51E1E] hover:bg-[#D41212] text-white px-8">
                                <Building2 className="w-5 h-5 mr-2" />
                                Post a Project
                            </Button>
                        </Link>
                        <Link to="/pricing">
                            <Button size="lg" variant="outline" className="border-[#444] text-white hover:bg-[#1A1A1A] px-8">
                                View Pricing
                            </Button>
                        </Link>
                    </div>

                    <p className="mt-6 text-sm text-[#666]">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Artists: Request access through a music supervisor or contact us
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#222] py-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F51E1E] to-[#FF6B6B] flex items-center justify-center">
                                <Music className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-bold text-white">Pitch Room</span>
                        </div>

                        <div className="flex items-center gap-8 text-sm text-[#888]">
                            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                            <Link to="/poster-signup" className="hover:text-white transition-colors">For Music Buyers</Link>
                            <Link to="/login" className="hover:text-white transition-colors">Login</Link>
                            <a href="mailto:support@pitchroom.com" className="hover:text-white transition-colors">Contact</a>
                        </div>

                        <p className="text-sm text-[#666]">
                            © 2026 Pitch Room. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
